import { Label } from "@radix-ui/react-label";
import { useId, type ComponentProps } from "react";
import { useFormContext, type FieldValues, type Path } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

type FormFileFieldBaseProps = Omit<ComponentProps<typeof Input>, "type">;

interface ArFormFileFieldProps<T extends FieldValues>
    extends FormFileFieldBaseProps {
    name: Path<T>;
    label: string;
    labelAlign?: "items-end" | "items-start" | "items-center";
}

export function ArFileFormField<T extends FieldValues>({
    label,
    name,
    labelAlign,
    onClick,
    ...rest
}: ArFormFileFieldProps<T>) {
    const {
        register,
        formState: { errors },
    } = useFormContext<T>();

    const error = errors[name as keyof typeof errors];
    const id = useId();

    const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onClick?.(e);
    };

    return (
        <div
            className={cn("flex flex-col space-y-2", labelAlign && labelAlign)}
        >
            <Label
                htmlFor={id}
                className={cn(
                    "text-sm font-medium",
                    error && "text-destructive",
                )}
            >
                {label}
            </Label>
            <Input
                id={id}
                type="file"
                className={cn(
                    error &&
                        "border-destructive focus-visible:ring-destructive",
                )}
                {...register(name)}
                onClick={handleClick}
                {...rest}
            />
            {error && (
                <p id={`${id}-error`} className="text-xs text-destructive">
                    {error.message as string}
                </p>
            )}
        </div>
    );
}
